import axios from "axios";

/** msOrg GET /auth/me 的验票结果（供守卫区分「未验证成功」与「令牌无效」） */
export type VerifySessionResult =
  | { kind: "ok" }
  | { kind: "invalid" }
  | { kind: "transport_error"; message?: string };

/** Vite 构建（import.meta.env）；CRA 无此项时为空，避免误用 */
export function readViteEnv(key: string): string {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | boolean | undefined> }).env;
    const v = env?.[key];
    return typeof v === "string" ? v.trim() : "";
  } catch {
    return "";
  }
}

export interface WecomLoginPayload {
  code: string;
  /** msOrg applications.id，换票 Secret 在服务端读取 */
  applicationId: string;
}

export interface WecomLoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: Record<string, unknown>;
}

/** 各业务站点可透传覆盖环境变量 */
export type WecomMsOrgClientOptions = {
  applicationId?: string;
  msOrgApiBaseUrl?: string;
  /** 企微 OAuth redirect_uri，不配则 env 或 `${origin}/wecom-callback` */
  wecomRedirectUri?: string;
  /** OAuth state 前缀，默认 msapp */
  oauthStatePrefix?: string;
  /**
   * GET /auth/me 浏览器侧超时（毫秒）。默认 12000，与 CRM 后端中央探测一致；
   * 链路过慢时按传输失败处理，避免等满 30s 仍「验票成功」进入。
   * 可配 REACT_APP_MSORG_SESSION_VERIFY_TIMEOUT_MS / VITE_MSORG_SESSION_VERIFY_TIMEOUT_MS。
   */
  sessionVerifyTimeoutMs?: number;
};

export function getMsOrgApiBaseUrl(
  options?: Pick<WecomMsOrgClientOptions, "msOrgApiBaseUrl">,
): string {
  const fromOpts = options?.msOrgApiBaseUrl?.trim();
  const fromEnv =
    (typeof process !== "undefined" && process.env?.REACT_APP_MSORG_API_BASE_URL?.trim()) ||
    readViteEnv("VITE_MSORG_API_BASE_URL") ||
    "";
  const v = fromOpts || fromEnv;
  if (v) return v.replace(/\/$/, "");
  return "/api/msorg";
}

export function resolveApplicationId(
  options?: Pick<WecomMsOrgClientOptions, "applicationId">,
): string {
  const v =
    options?.applicationId?.trim() ||
    (typeof process !== "undefined" && process.env?.REACT_APP_MSORG_APPLICATION_ID?.trim()) ||
    readViteEnv("VITE_MSORG_APPLICATION_ID") ||
    "";
  return v;
}

function getSessionVerifyTimeoutMs(
  options?: Pick<WecomMsOrgClientOptions, "sessionVerifyTimeoutMs">,
): number {
  const fromOpts = options?.sessionVerifyTimeoutMs;
  if (typeof fromOpts === "number" && Number.isFinite(fromOpts) && fromOpts > 0) {
    return Math.floor(fromOpts);
  }
  const envRaw =
    (typeof process !== "undefined" && process.env?.REACT_APP_MSORG_SESSION_VERIFY_TIMEOUT_MS?.trim()) ||
    readViteEnv("VITE_MSORG_SESSION_VERIFY_TIMEOUT_MS");
  const fromEnv = envRaw ? Number(envRaw) : 0;
  if (Number.isFinite(fromEnv) && fromEnv > 0) {
    return Math.floor(fromEnv);
  }
  /** 与 msCrm `msOrgCentralSessionProbe.PROBE_TIMEOUT_MS` 对齐 */
  return 12_000;
}

/** sessionStorage 中短时跳过重复 GET /auth/me 的标记（同标签页刷新仍保留，显著降低「每次刷新 2～3 秒」体感） */
const SESSION_VERIFY_CACHE_KEY = "msorg_verify_ok_v1";

function tokenFingerprint(token: string): string {
  let h = 2166136261;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${h >>> 0}:${token.length}`;
}

function readMsOrgVerifyCacheTtlMs(): number {
  const raw =
    (typeof process !== "undefined" && process.env?.REACT_APP_MSORG_SESSION_VERIFY_CACHE_MS?.trim()) ||
    readViteEnv("VITE_MSORG_SESSION_VERIFY_CACHE_MS");
  const n = raw ? Number(raw) : 0;
  if (Number.isFinite(n) && n > 0) {
    return Math.floor(n);
  }
  return 90_000;
}

function tryReadSessionVerifyCache(token: string): boolean {
  try {
    if (typeof sessionStorage === "undefined") return false;
    const raw = sessionStorage.getItem(SESSION_VERIFY_CACHE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { fp?: string; ts?: number };
    if (typeof parsed.fp !== "string" || typeof parsed.ts !== "number") return false;
    if (parsed.fp !== tokenFingerprint(token)) return false;
    if (Date.now() - parsed.ts > readMsOrgVerifyCacheTtlMs()) return false;
    return true;
  } catch {
    return false;
  }
}

function writeSessionVerifyCache(token: string): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(
      SESSION_VERIFY_CACHE_KEY,
      JSON.stringify({ fp: tokenFingerprint(token), ts: Date.now() }),
    );
  } catch {
    /* 企微 WebView 可能禁用 storage */
  }
}

function clearSessionVerifyCache(): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(SESSION_VERIFY_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** 登出/清票时调用，避免残留「已验票」标记 */
export function clearMsOrgSessionVerifyCache(): void {
  clearSessionVerifyCache();
}

/** 同一 token 并发验票合并为单次请求（如 React Strict Mode 双挂载） */
const inFlightVerify = new Map<string, Promise<VerifySessionResult>>();

export async function wecomLogin(
  payload: WecomLoginPayload,
  options?: Pick<WecomMsOrgClientOptions, "msOrgApiBaseUrl">,
): Promise<WecomLoginResponse> {
  const base = getMsOrgApiBaseUrl(options);
  const res = await axios.post<WecomLoginResponse>(`${base}/auth/wecom-login`, payload, {
    headers: { "Content-Type": "application/json" },
    timeout: 20000,
  });
  return res.data;
}

/**
 * 调 msOrg GET /auth/me。
 * - invalid：401/403 或明确失败 → 可清票后走企微 OAuth
 * - transport_error：超时/断网/5xx → **不得**自动跳转 OAuth，否则用户未经验证又换票进入
 */
async function verifySessionWithMsOrgUncached(
  token: string,
  options?: Pick<WecomMsOrgClientOptions, "msOrgApiBaseUrl" | "sessionVerifyTimeoutMs">,
): Promise<VerifySessionResult> {
  const base = getMsOrgApiBaseUrl(options);
  try {
    const res = await axios.get<{ success?: boolean }>(`${base}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: getSessionVerifyTimeoutMs(options),
    });
    if (res.data?.success === true) {
      writeSessionVerifyCache(token);
      return { kind: "ok" };
    }
    clearSessionVerifyCache();
    return { kind: "invalid" };
  } catch (e: unknown) {
    if (axios.isAxiosError(e)) {
      const st = e.response?.status;
      if (st === 401 || st === 403) {
        clearSessionVerifyCache();
        return { kind: "invalid" };
      }
      if (!e.response || e.code === "ECONNABORTED" || e.code === "ERR_NETWORK") {
        return { kind: "transport_error", message: e.message };
      }
      if (st != null && st >= 500) {
        return { kind: "transport_error", message: `HTTP ${st}` };
      }
      if (st != null && st >= 400) {
        clearSessionVerifyCache();
        return { kind: "invalid" };
      }
    }
    return {
      kind: "transport_error",
      message: e instanceof Error ? e.message : String(e),
    };
  }
}

export async function verifySessionWithMsOrg(
  token: string,
  options?: Pick<WecomMsOrgClientOptions, "msOrgApiBaseUrl" | "sessionVerifyTimeoutMs">,
): Promise<VerifySessionResult> {
  const t = token.trim();
  if (!t) {
    return { kind: "invalid" };
  }

  if (tryReadSessionVerifyCache(t)) {
    return { kind: "ok" };
  }

  const fp = tokenFingerprint(t);
  let p = inFlightVerify.get(fp);
  if (!p) {
    p = verifySessionWithMsOrgUncached(t, options).finally(() => {
      inFlightVerify.delete(fp);
    });
    inFlightVerify.set(fp, p);
  }
  return p;
}

/** 仅关心是否通过；超时/网络错误也视为 false（旧逻辑，非守卫场景慎用） */
export async function verifyCurrentUser(
  token: string,
  options?: Pick<WecomMsOrgClientOptions, "msOrgApiBaseUrl" | "sessionVerifyTimeoutMs">,
): Promise<boolean> {
  const r = await verifySessionWithMsOrg(token, options);
  return r.kind === "ok";
}
